'use client';

import { useTranslation } from 'react-i18next';
import { PortalLink, useLocale, coverBackground } from '@akademi/site-builder-runtime';
import { Button, Card } from '@akademi/ui';
import {
  ShoppingCart,
  X,
  PlayCircle,
  Users,
  ThumbsUp,
  ThumbsDown,
  FolderClosed,
  Globe,
  Layers,
  Home,
  type LucideIcon,
} from 'lucide-react';
import { tx } from '../_shared/coerce';
import { cardReset } from '../_shared/styles';
import type { CourseCardProps } from '../cards/CourseCard.types';
import { formatMarketPrice, type MarketPrice } from './_shared';
import { MARKET_CARD_CSS } from './_marketCardCss';

type Localized = string | Record<string, string>;

/** Unified marketplace tile — a 1:1 match of akademi.ws `/catalog`'s card:
 *  image + type-badge chip, participant + thumbs-up/down meta row (or a
 *  start/end-date pair for a live course), price ("Ücretsiz" or an amount
 *  with an optional struck was-price), a divider, and a footer with the
 *  provider name + logo and an add-to-cart icon. */
export interface MarketplaceCardData {
  id?: string;
  image?: string;
  /** Small pill on the image, bottom-left — e.g. "E-Öğrenme", "Program". */
  kindLabel?: string;
  kindIcon?: 'elearning' | 'program' | 'classroom' | 'video' | 'catalog';
  title?: Localized;
  attendeeCount?: number;
  courseCount?: number;
  likeCount?: number;
  dislikeCount?: number;
  startDate?: string;
  endDate?: string;
  provider?: string;
  providerLogo?: string;
  /** Display string ("Beginner", "İleri Seviye", …) shown next to the
   *  like/dislike meta row on the `marketplace` registry card style. */
  level?: string;
  category?: string;
  price?: MarketPrice;
  listPrice?: MarketPrice;
  free?: boolean;
  purchased?: boolean;
  href?: string;
}

const KIND_ICONS: Record<NonNullable<MarketplaceCardData['kindIcon']>, LucideIcon> = {
  elearning: Globe,
  program: Layers,
  classroom: Home,
  video: PlayCircle,
  catalog: FolderClosed,
};

function formatDate(iso: string, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Bridge into the `cards/` registry's wide `CourseCardProps` shape, so a
 *  marketplace grid can render one of its registered card variants (e.g.
 *  `MarketplaceCourseCard`) alongside this component's own default look. */
export function toCourseCardProps(item: MarketplaceCardData, locale: string): CourseCardProps {
  const priceText = item.free ? undefined : formatMarketPrice(item.price, locale);
  const listPriceText = item.free ? undefined : formatMarketPrice(item.listPrice, locale);
  return {
    id: item.id ?? '',
    title: tx(item.title),
    href: item.href ?? '#',
    image: item.image,
    enrolled: item.attendeeCount ?? item.courseCount,
    likeCount: item.likeCount,
    dislikeCount: item.dislikeCount,
    liveStartDate: item.startDate,
    liveEndDate: item.endDate,
    trainers: item.provider || item.category,
    // Repurposed for this variant only — the registry contract has no
    // dedicated "provider logo" field, and MarketplaceCourseCard never
    // renders an instructor avatar.
    instructorAvatar: item.providerLogo,
    level: item.level,
    badge: item.kindLabel ? { label: item.kindLabel } : undefined,
    price: priceText,
    listPrice: listPriceText,
  };
}

export function MarketplaceCard({
  item,
  inCart,
  onToggleCart,
}: {
  item: MarketplaceCardData;
  inCart: boolean;
  onToggleCart: () => void;
  starSide?: 'left' | 'right';
}) {
  const { t } = useTranslation('site-builder-blocks');
  const { locale } = useLocale();

  const priceText = formatMarketPrice(item.price, locale);
  const listPriceText = formatMarketPrice(item.listPrice, locale);
  const showDiscount = Boolean(listPriceText && priceText && listPriceText !== priceText);
  const isLive = Boolean(item.startDate);

  const KindIcon = item.kindIcon ? KIND_ICONS[item.kindIcon] : Globe;
  const kindLabel =
    item.kindLabel ??
    (item.kindIcon === 'catalog'
      ? t('card.marketplace.program', { defaultValue: 'Program' })
      : t('card.marketplace.elearning', { defaultValue: 'E-Learning' }));

  const provider = item.provider || item.category;

  const inner = (
    <Card
      className="mp-card"
      style={{
        ...cardReset,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        height: '100%',
        borderRadius: 'var(--radius-lg, 12px)',
        background: 'var(--color-surface-card, #fff)',
        border: '1px solid var(--color-border-default, #e2e8f0)',
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: MARKET_CARD_CSS }} />

      <div
        aria-hidden={!item.image}
        style={{
          position: 'relative',
          aspectRatio: '16 / 10',
          overflow: 'hidden',
          background: 'var(--color-surface-muted, #eef2f6)',
          ...(item.image ? coverBackground(item.image) : {}),
        }}
      >
        <span
          style={{
            position: 'absolute',
            left: 10,
            bottom: 10,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '4px 10px',
            borderRadius: 'var(--radius-full, 999px)',
            background: 'rgba(255,255,255,0.92)',
            color: 'var(--color-brand-600, #287f96)',
            fontSize: 'var(--font-size-xs, 12px)',
            fontWeight: 600,
            boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
          }}
        >
          <KindIcon size={13} />
          {kindLabel}
        </span>
      </div>

      <div
        style={{
          flex: 1,
          padding: 'var(--space-4, 16px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3, 12px)',
        }}
      >
        <div
          style={{
            fontSize: 'var(--font-size-md, 16px)',
            fontWeight: 700,
            color: 'var(--color-text-primary, #1c1d1f)',
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {tx(item.title)}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-4, 16px)',
            fontSize: 'var(--font-size-sm, 13px)',
            color: 'var(--color-text-tertiary, #6a6f73)',
          }}
        >
          {typeof item.attendeeCount === 'number' && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <Users size={15} />
              {item.attendeeCount.toLocaleString(locale)}
            </span>
          )}
          {typeof item.courseCount === 'number' && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <FolderClosed size={15} />
              {item.courseCount.toLocaleString(locale)}
            </span>
          )}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <ThumbsUp size={15} />
            {(item.likeCount ?? 0).toLocaleString(locale)}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <ThumbsDown size={15} />
            {(item.dislikeCount ?? 0).toLocaleString(locale)}
          </span>
        </div>

        {isLive && (
          <div style={{ display: 'flex', gap: 'var(--space-5, 24px)' }}>
            {[
              { label: t('card.marketplace.startDate'), value: item.startDate },
              { label: t('card.marketplace.endDate'), value: item.endDate },
            ].map(
              (col) =>
                col.value && (
                  <div key={col.label}>
                    <div
                      style={{
                        fontSize: 'var(--font-size-xs, 12px)',
                        color: 'var(--color-text-tertiary, #6a6f73)',
                      }}
                    >
                      {col.label}
                    </div>
                    <div
                      style={{
                        fontSize: 'var(--font-size-sm, 13px)',
                        fontWeight: 600,
                        color: 'var(--color-text-primary, #1c1d1f)',
                      }}
                    >
                      {formatDate(col.value, locale)}
                    </div>
                  </div>
                ),
            )}
          </div>
        )}

        <div
          style={{
            marginTop: 'auto',
            display: 'flex',
            alignItems: 'baseline',
            gap: 'var(--space-2, 8px)',
          }}
        >
          <span
            style={{
              fontSize: 'var(--font-size-lg, 18px)',
              fontWeight: 700,
              color: 'var(--color-text-primary, #1c1d1f)',
            }}
          >
            {item.free || !priceText ? t('card.marketplace.free') : priceText}
          </span>
          {!item.free && showDiscount && (
            <span
              style={{
                fontSize: 'var(--font-size-sm, 14px)',
                color: 'var(--color-text-tertiary, #9ca3af)',
                textDecoration: 'line-through',
              }}
            >
              {listPriceText}
            </span>
          )}
        </div>
      </div>

      <div
        style={{
          borderTop: '1px solid var(--color-border-subtle, #eef2f6)',
          padding: 'var(--space-3, 12px) var(--space-4, 16px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-2, 8px)',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            minWidth: 0,
            fontSize: 'var(--font-size-sm, 13px)',
            fontWeight: 500,
            color: 'var(--color-text-secondary, #52525b)',
          }}
        >
          {provider && (
            <>
              <span
                aria-hidden
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: 6,
                  flexShrink: 0,
                  background:
                    'linear-gradient(135deg, var(--color-brand-500, #319db3), var(--color-brand-700, #1f6377))',
                }}
              />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {provider}
              </span>
            </>
          )}
        </span>
        {item.purchased ? (
          <Button asChild size="sm" variant="ghost">
            <PortalLink href={item.href ?? '#'}>
              <PlayCircle size={15} /> {t('marketplace.courseGrid.startCourse')}
            </PortalLink>
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="mp-cart-icon-btn"
            aria-label={
              inCart
                ? t('marketplace.catalogCard.removeFromCart')
                : t('marketplace.catalogCard.addToCart')
            }
            style={{ flexShrink: 0, color: 'var(--color-text-tertiary, #6a6f73)' }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleCart();
            }}
          >
            <span className="mp-cart-glyph" style={{ display: 'inline-flex' }}>
              {inCart ? <X size={17} /> : <ShoppingCart size={17} />}
            </span>
          </Button>
        )}
      </div>
    </Card>
  );

  return item.href && !item.purchased ? (
    <PortalLink
      href={item.href}
      style={{ color: 'inherit', textDecoration: 'none', display: 'block', height: '100%' }}
    >
      {inner}
    </PortalLink>
  ) : (
    inner
  );
}
